import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { supabase } from "../../lib/supabase";
import { SetPasswordModal } from "../../components/SetPasswordModal";

/**
 * Landing page for Supabase magic links and invite emails.
 * No auth guard — Supabase redirects here with tokens in the URL hash.
 * We let the client process the session, then route the user appropriately.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [invitedBy, setInvitedBy] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState<string | null>(null);
  const [defaultName, setDefaultName] = useState<string | null>(null);
  const [defaultEmail, setDefaultEmail] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    handledRef.current = false;

    const handleSession = async (session: NonNullable<Awaited<ReturnType<typeof supabase.auth.getSession>>["data"]["session"]>) => {
      if (handledRef.current) return;
      handledRef.current = true;

      const meta = session.user.user_metadata ?? {};
      const isInvite = window.location.hash.includes("type=invite");

      // Clean the hash regardless
      window.history.replaceState(null, "", window.location.pathname);

      if (isInvite) {
        // Show the set-password modal before continuing
        setInvitedBy(meta.invited_by ?? null);
        setProjectTitle(meta.project_title ?? null);
        setDefaultName(meta.full_name ?? meta.name ?? null);
        setDefaultEmail(session.user.email ?? null);
        setShowSetPassword(true);
      } else {
        // Normal magic link — route to the right dashboard
        const role = meta.role ?? "client";
        navigate(role === "contractor" ? "/contractor" : "/client", {
          replace: true,
        });
      }
    };

    // Supabase may establish the callback session via INITIAL_SESSION,
    // SIGNED_IN, or TOKEN_REFRESHED depending on timing and refresh state.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (
          session &&
          (event === "SIGNED_IN" ||
            event === "INITIAL_SESSION" ||
            event === "TOKEN_REFRESHED")
        ) {
          await handleSession(session);
        }
      },
    );

    void supabase.auth.getSession().then(async ({ data }) => {
      if (data.session) {
        await handleSession(data.session);
      } else if (!handledRef.current) {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handlePasswordSet = () => {
    setShowSetPassword(false);
    navigate("/client", { replace: true });
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Setting up your account…
        </Typography>
      </Box>

      <SetPasswordModal
        open={showSetPassword}
        invitedBy={invitedBy}
        projectTitle={projectTitle}
        defaultName={defaultName}
        defaultEmail={defaultEmail}
        onComplete={handlePasswordSet}
      />
    </>
  );
}
